<template>
    <section>
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="animate-in fade-in slide-in-from-bottom-2 mb-16 duration-500">
                <SectionHeader title="Information" prefix="Event!" :icon="Info" />
                <div class="event-content text-body leading-relaxed whitespace-pre-line"
                    v-html="event.content || event.description">
                </div>
            </div>
    
            <!-- Details card (part of the About tab) -->
            <aside class="lg:col-span-1 mb-16">
                <div class="lg:sticky lg:top-24 rounded-3xl border border-app bg-card/70 backdrop-blur-2xl p-7 shadow-2xl space-y-6">
                    <h3 class="text-sm font-bold text-header uppercase tracking-widest">Event details</h3>
                    <div class="grid grid-cols-1 md:grid-cols-3 space-y-4 md:space-y-0">
    
                        <div class="flex items-start gap-3.5">
                            <div class="p-2 rounded-lg bg-brand-500/10 text-brand-500 shrink-0">
                                <Calendar class="h-5 w-5" />
                            </div>
                            <div>
                                <div class="text-xs text-body/80 font-bold uppercase tracking-wider mb-0.5">Date</div>
                                <div class="text-header font-semibold text-sm">{{ dateRange || 'To be announced' }}</div>
                            </div>
                        </div>
    
                        <div class="flex items-start gap-3.5">
                            <div class="p-2 rounded-lg bg-brand-500/10 text-brand-500 shrink-0">
                                <Clock class="h-5 w-5" />
                            </div>
                            <div>
                                <div class="text-xs text-body/80 font-bold uppercase tracking-wider mb-0.5">Time</div>
                                <div class="text-header font-semibold text-sm">
                                    {{ formatTime(event.startDate) || 'TBA' }}
                                    <template v-if="event.endDate"> – {{ formatTime(event.endDate) }}</template>
                                </div>
                            </div>
                        </div>
    
                        <div v-if="event.location" class="flex items-start gap-3.5">
                            <div class="p-2 rounded-lg bg-brand-500/10 text-brand-500 shrink-0">
                                <MapPin class="h-5 w-5" />
                            </div>
                            <div>
                                <div class="text-xs text-body/80 font-bold uppercase tracking-wider mb-0.5">Location</div>
                                <div class="text-header font-semibold text-sm leading-snug">{{ event.location }}</div>
                            </div>
                        </div>
                    </div>
    
                    <NuxtLink v-if="false" :to="{ name: 'Membership' }"
                        class="block text-center bg-brand-500 text-white px-6 py-3.5 rounded-2xl font-bold hover:bg-brand-600 transition-all shadow-xl active:scale-95">
                        Register your interest
                    </NuxtLink>
                </div>
            </aside>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Calendar, Clock, Info, MapPin } from 'lucide-vue-next';
import SectionHeader from './SectionHeader.vue';

const props = defineProps<{
    event: import('~/types').EventItem;
}>();

const formatDate = (isoDate?: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('default', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatTime = (isoDate?: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const dateRange = computed(() => {
    const start = formatDate(props.event?.startDate);
    const end = formatDate(props.event?.endDate);
    if (!start) return '';
    if (end && end !== start) return `${start} – ${end}`;
    return start;
});

</script>